import { supabase } from '../supabase';
import { config } from '../config';
import type { Review, ReviewWithRelations, ReviewInsert, ReviewUpdate } from '../types';

/**
 * Get all approved reviews (public)
 */
export const getApprovedReviews = async (filters?: {
  productId?: string;
  batchId?: string;
  limit?: number;
}): Promise<ReviewWithRelations[]> => {
  // 🔧 DEVELOPMENT MODE - Return mock reviews
  if (config.devMode.enabled && config.devMode.mockData) {
    console.log('🔧 DEV MODE: Returning mock approved reviews', filters);
    
    const mockReviews: ReviewWithRelations[] = [
      {
        id: 'review-1',
        author_id: 'mock-admin-123',
        product_id: 'product-1',
        batch_id: 'batch-1-1',
        stars: 5,
        title: 'Excellent strain for daytime use',
        body: 'Blue Dream is perfect for managing anxiety while staying productive. Great quality from Premium Cannabis Co.',
        review_text: 'Blue Dream is perfect for managing anxiety while staying productive. Great quality from Premium Cannabis Co.',
        effects: null,
        mod_state: 'approved' as const,
        moderated_by: null,
        moderated_at: null,
        mod_notes: null,
        is_flagged: false,
        flagged_at: null,
        consent_version: '1.0',
        consented_at: new Date().toISOString(),
        submitted_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        created_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        updated_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        product: {
          id: 'product-1',
          sku: 'MWL-001',
          product_name: 'Blue Dream',
          brand: 'Premium Cannabis Co',
          form: 'flower' as const,
          thc_pct: 18.5,
          cbd_pct: 0.5,
          pack_size: '3.5g',
          active: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        batch: {
          id: 'batch-1-1',
          product_id: 'product-1',
          batch_no: 'BD-2024-Q4-001',
          received_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          expiry_date: new Date(Date.now() + 335 * 24 * 60 * 60 * 1000).toISOString(),
          status: 'in_stock' as const,
          coa_url: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        author: {
          id: 'mock-admin-123',
          display_name: 'Admin User',
          role: 'admin' as const,
        },
      },
      {
        id: 'review-2',
        author_id: 'user-2',
        product_id: 'product-1',
        batch_id: null,
        stars: 4,
        title: 'Good quality, consistent effects',
        body: 'Very consistent across batches. Helps with pain management without being too sedating.',
        review_text: 'Very consistent across batches. Helps with pain management without being too sedating.',
        effects: null,
        mod_state: 'approved' as const,
        moderated_by: null,
        moderated_at: null,
        mod_notes: null,
        is_flagged: false,
        flagged_at: null,
        consent_version: '1.0',
        consented_at: new Date().toISOString(),
        submitted_at: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
        created_at: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
        updated_at: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
        product: {
          id: 'product-1',
          sku: 'MWL-001',
          product_name: 'Blue Dream',
          brand: 'Premium Cannabis Co',
          form: 'flower' as const,
          thc_pct: 18.5,
          cbd_pct: 0.5,
          pack_size: '3.5g',
          active: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        batch: null,
        author: {
          id: 'user-2',
          display_name: 'Patient User',
          role: 'patient' as const,
        },
      },
    ];
    
    // Apply filters
    let filtered = mockReviews;
    
    if (filters?.productId) {
      filtered = filtered.filter(r => r.product_id === filters.productId);
    }
    
    if (filters?.batchId) {
      filtered = filtered.filter(r => r.batch_id === filters.batchId);
    }
    
    if (filters?.limit) {
      filtered = filtered.slice(0, filters.limit);
    }
    
    console.log(`✅ Returning ${filtered.length} mock reviews`);
    return filtered;
  }
  
  let query = supabase
    .from('reviews')
    .select(`
      *,
      product:products(*),
      batch:batches(*),
      author:profiles!reviews_author_id_fkey(id, display_name, role)
    `)
    .eq('mod_state', 'approved');

  if (filters?.productId) {
    query = query.eq('product_id', filters.productId);
  }

  if (filters?.batchId) {
    query = query.eq('batch_id', filters.batchId);
  }

  if (filters?.limit) {
    query = query.limit(filters.limit);
  }

  const { data, error } = await query.order('submitted_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

/**
 * Get review by ID
 */
export const getReviewById = async (id: string): Promise<ReviewWithRelations | null> => {
  const { data, error } = await supabase
    .from('reviews')
    .select(`
      *,
      product:products(*),
      batch:batches(*),
      author:profiles!reviews_author_id_fkey(id, display_name, role),
      moderator:profiles!reviews_moderated_by_fkey(id, display_name, role)
    `)
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }

  return data;
};

/**
 * Get reviews by user
 */
export const getReviewsByUser = async (userId: string): Promise<ReviewWithRelations[]> => {
  const { data, error } = await supabase
    .from('reviews')
    .select(`
      *,
      product:products(*),
      batch:batches(*)
    `)
    .eq('author_id', userId)
    .order('submitted_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

/**
 * Create review (patient only)
 */
export const createReview = async (review: ReviewInsert): Promise<Review> => {
  // 🔧 DEVELOPMENT MODE - Return mock review
  if (config.devMode.enabled && config.devMode.mockData) {
    console.log('🔧 DEV MODE: Creating mock review', review);
    const mockReview: Review = {
      id: `review-${Date.now()}`,
      author_id: review.author_id,
      product_id: review.product_id,
      batch_id: review.batch_id || null,
      stars: review.stars,
      title: review.title || null,
      body: review.body || null,
      review_text: review.body || null,
      effects: null,
      mod_state: review.mod_state || 'approved',
      moderated_by: null,
      moderated_at: null,
      mod_notes: null,
      is_flagged: false,
      flagged_at: null,
      consent_version: review.consent_version || '1.0',
      consented_at: review.consented_at || new Date().toISOString(),
      submitted_at: new Date().toISOString(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    console.log('✅ Mock review created:', mockReview.id);
    return mockReview;
  }
  
  const { data, error } = await supabase
    .from('reviews')
    .insert(review)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Update review (author only, within 15 minutes)
 */
export const updateReview = async (id: string, updates: ReviewUpdate): Promise<Review> => {
  const { data, error } = await supabase
    .from('reviews')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Delete review (author only, within 15 minutes)
 */
export const deleteReview = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('reviews')
    .delete()
    .eq('id', id);

  if (error) throw error;
};

/**
 * Get all reviews (admin/moderator only)
 */
export const getAllReviews = async (): Promise<ReviewWithRelations[]> => {
  // 🔧 DEVELOPMENT MODE - Return mock reviews for moderation
  if (config.devMode.enabled && config.devMode.mockData) {
    console.log('🔧 DEV MODE: Returning mock reviews for moderation queue');
    
    const mockReviews: ReviewWithRelations[] = [
      // Pending Review 1
      {
        id: 'review-pending-1',
        author_id: 'user-1',
        product_id: 'product-1',
        batch_id: 'batch-1-1',
        stars: 5,
        title: 'Excellent for chronic pain',
        body: 'This strain has been a game-changer for my chronic back pain. The effects are long-lasting and I can still function normally throughout the day.',
        review_text: 'This strain has been a game-changer for my chronic back pain. The effects are long-lasting and I can still function normally throughout the day.',
        effects: ['pain-relief', 'relaxed', 'focused'],
        mod_state: 'pending' as const,
        moderated_by: null,
        moderated_at: null,
        mod_notes: null,
        is_flagged: false,
        flagged_at: null,
        consent_version: '1.0',
        consented_at: new Date().toISOString(),
        submitted_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
        created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        updated_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        product: {
          id: 'product-1',
          sku: 'MWL-001',
          product_name: 'Blue Dream',
          brand: 'Premium Cannabis Co',
          form: 'flower' as const,
          thc_pct: 18.5,
          cbd_pct: 0.5,
          pack_size: '3.5g',
          active: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        batch: {
          id: 'batch-1-1',
          product_id: 'product-1',
          batch_no: 'BD-2024-Q4-001',
          received_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          expiry_date: new Date(Date.now() + 335 * 24 * 60 * 60 * 1000).toISOString(),
          status: 'in_stock' as const,
          coa_url: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        author: {
          id: 'user-1',
          display_name: 'Sarah Johnson',
          role: 'patient' as const,
        },
      },
      // Pending Review 2
      {
        id: 'review-pending-2',
        author_id: 'user-2',
        product_id: 'product-2',
        batch_id: null,
        stars: 4,
        title: 'Good for anxiety',
        body: 'Helps with my anxiety without making me too drowsy. Can use during the day.',
        review_text: 'Helps with my anxiety without making me too drowsy. Can use during the day.',
        effects: ['calm', 'focused'],
        mod_state: 'pending' as const,
        moderated_by: null,
        moderated_at: null,
        mod_notes: null,
        is_flagged: false,
        flagged_at: null,
        consent_version: '1.0',
        consented_at: new Date().toISOString(),
        submitted_at: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(), // 5 hours ago
        created_at: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
        updated_at: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
        product: {
          id: 'product-2',
          sku: 'MWL-002',
          product_name: 'Northern Lights',
          brand: 'Green Wellness',
          form: 'flower' as const,
          thc_pct: 16.0,
          cbd_pct: 1.0,
          pack_size: '5g',
          active: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        batch: null,
        author: {
          id: 'user-2',
          display_name: 'Mike Peters',
          role: 'patient' as const,
        },
      },
      // Flagged Review
      {
        id: 'review-flagged-1',
        author_id: 'user-3',
        product_id: 'product-1',
        batch_id: null,
        stars: 1,
        title: 'Not what I expected',
        body: 'This is a miracle cure for everything! Guaranteed to work!',
        review_text: 'This is a miracle cure for everything! Guaranteed to work!',
        effects: null,
        mod_state: 'pending' as const,
        moderated_by: null,
        moderated_at: null,
        mod_notes: null,
        is_flagged: true,
        flagged_at: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
        consent_version: '1.0',
        consented_at: new Date().toISOString(),
        submitted_at: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
        created_at: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
        updated_at: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
        product: {
          id: 'product-1',
          sku: 'MWL-001',
          product_name: 'Blue Dream',
          brand: 'Premium Cannabis Co',
          form: 'flower' as const,
          thc_pct: 18.5,
          cbd_pct: 0.5,
          pack_size: '3.5g',
          active: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        batch: null,
        author: {
          id: 'user-3',
          display_name: 'Anonymous User',
          role: 'patient' as const,
        },
      },
      // Approved Review
      {
        id: 'review-approved-1',
        author_id: 'user-4',
        product_id: 'product-2',
        batch_id: null,
        stars: 5,
        title: 'Excellent strain',
        body: 'Very consistent effects. Helps me sleep through the night without grogginess in the morning.',
        review_text: 'Very consistent effects. Helps me sleep through the night without grogginess in the morning.',
        effects: ['sleepy', 'relaxed', 'pain-relief'],
        mod_state: 'approved' as const,
        moderated_by: '00000000-0000-0000-0000-000000000099', // Admin user
        moderated_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
        mod_notes: 'Approved - meets all guidelines',
        is_flagged: false,
        flagged_at: null,
        consent_version: '1.0',
        consented_at: new Date().toISOString(),
        submitted_at: new Date(Date.now() - 25 * 60 * 60 * 1000).toISOString(),
        created_at: new Date(Date.now() - 25 * 60 * 60 * 1000).toISOString(),
        updated_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
        product: {
          id: 'product-2',
          sku: 'MWL-002',
          product_name: 'Northern Lights',
          brand: 'Green Wellness',
          form: 'flower' as const,
          thc_pct: 16.0,
          cbd_pct: 1.0,
          pack_size: '5g',
          active: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        batch: null,
        author: {
          id: 'user-4',
          display_name: 'Emma Wilson',
          role: 'patient' as const,
        },
      },
      // Rejected Review
      {
        id: 'review-rejected-1',
        author_id: 'user-5',
        product_id: 'product-1',
        batch_id: null,
        stars: 1,
        title: 'Terrible product',
        body: 'This is spam content with inappropriate language and no medical relevance.',
        review_text: 'This is spam content with inappropriate language and no medical relevance.',
        effects: null,
        mod_state: 'rejected' as const,
        moderated_by: '00000000-0000-0000-0000-000000000099', // Admin user
        moderated_at: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
        mod_notes: 'Rejected - spam content, violates community guidelines',
        is_flagged: false,
        flagged_at: null,
        consent_version: '1.0',
        consented_at: new Date().toISOString(),
        submitted_at: new Date(Date.now() - 13 * 60 * 60 * 1000).toISOString(),
        created_at: new Date(Date.now() - 13 * 60 * 60 * 1000).toISOString(),
        updated_at: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
        product: {
          id: 'product-1',
          sku: 'MWL-001',
          product_name: 'Blue Dream',
          brand: 'Premium Cannabis Co',
          form: 'flower' as const,
          thc_pct: 18.5,
          cbd_pct: 0.5,
          pack_size: '3.5g',
          active: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        batch: null,
        author: {
          id: 'user-5',
          display_name: 'Spam Account',
          role: 'patient' as const,
        },
      },
    ];
    
    console.log(`✅ Returning ${mockReviews.length} mock reviews for moderation (${mockReviews.filter(r => r.mod_state === 'pending').length} pending, ${mockReviews.filter(r => r.is_flagged).length} flagged)`);
    return mockReviews;
  }
  
  const { data, error } = await supabase
    .from('reviews')
    .select(`
      *,
      product:products(*),
      batch:batches(*),
      author:profiles!reviews_author_id_fkey(id, display_name, role)
    `)
    .order('submitted_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

/**
 * Get pending reviews (admin/moderator only)
 */
export const getPendingReviews = async (): Promise<ReviewWithRelations[]> => {
  const { data, error } = await supabase
    .from('reviews')
    .select(`
      *,
      product:products(*),
      batch:batches(*),
      author:profiles!reviews_author_id_fkey(id, display_name, role)
    `)
    .eq('mod_state', 'pending')
    .order('submitted_at', { ascending: true });

  if (error) throw error;
  return data || [];
};

/**
 * Get flagged reviews (admin/moderator only)
 */
export const getFlaggedReviews = async (): Promise<ReviewWithRelations[]> => {
  const { data, error } = await supabase
    .from('reviews')
    .select(`
      *,
      product:products(*),
      batch:batches(*),
      author:profiles!reviews_author_id_fkey(id, display_name, role)
    `)
    .eq('is_flagged', true)
    .order('flagged_at', { ascending: true });

  if (error) throw error;
  return data || [];
};

/**
 * Get user's reviews
 */
export const getUserReviews = async (userId: string): Promise<ReviewWithRelations[]> => {
  const { data, error } = await supabase
    .from('reviews')
    .select(`
      *,
      product:products(*),
      batch:batches(*),
      author:profiles!reviews_author_id_fkey(id, display_name, role)
    `)
    .eq('author_id', userId)
    .order('submitted_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

/**
 * Moderate review (admin/moderator only)
 */
export const moderateReview = async (
  id: string,
  updates: {
    mod_state: 'approved' | 'rejected';
    mod_notes?: string;
  }
): Promise<Review> => {
  // 🔧 DEVELOPMENT MODE - Simulate moderation
  if (config.devMode.enabled && config.devMode.mockData) {
    console.log(`🔧 DEV MODE: Moderating review ${id} as ${updates.mod_state}`);
    const mockReview: Review = {
      id,
      author_id: 'user-1',
      product_id: 'product-1',
      batch_id: null,
      stars: 5,
      title: 'Mock Review',
      body: 'This is a mock review for testing',
      review_text: 'This is a mock review for testing',
      effects: null,
      mod_state: updates.mod_state,
      moderated_by: '00000000-0000-0000-0000-000000000099', // Admin user
      moderated_at: new Date().toISOString(),
      mod_notes: updates.mod_notes || null,
      is_flagged: false,
      flagged_at: null,
      consent_version: '1.0',
      consented_at: new Date().toISOString(),
      submitted_at: new Date().toISOString(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    console.log(`✅ Review ${id} ${updates.mod_state}`);
    return mockReview;
  }
  
  const { data: { user } } = await supabase.auth.getUser();
  
  const { data, error } = await supabase
    .from('reviews')
    .update({
      mod_state: updates.mod_state,
      moderated_by: user?.id,
      moderated_at: new Date().toISOString(),
      mod_notes: updates.mod_notes,
      is_flagged: false, // Clear flag when moderated
    })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Flag review
 */
export const flagReview = async (id: string): Promise<Review> => {
  const { data, error } = await supabase
    .from('reviews')
    .update({
      is_flagged: true,
      flagged_at: new Date().toISOString(),
    })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Check if content contains blocked phrases
 */
export const checkForBlockedPhrases = async (content: string): Promise<{
  hasBlocked: boolean;
  matches: Array<{ phrase: string; severity: string }>;
}> => {
  // 🔧 DEVELOPMENT MODE - Use mock blocked phrases
  if (config.devMode.enabled && config.devMode.mockData) {
    console.log('🔧 DEV MODE: Checking for blocked phrases in:', content.substring(0, 50) + '...');
    
    // Mock blocked phrases for testing
    const mockBlockedPhrases = [
      { phrase: 'cure', severity: 'critical' },
      { phrase: 'miracle', severity: 'critical' },
      { phrase: 'guaranteed', severity: 'moderate' },
      { phrase: 'amazing', severity: 'low' },
    ];
    
    const matches: Array<{ phrase: string; severity: string }> = [];
    const contentLower = content.toLowerCase();

    mockBlockedPhrases.forEach((phrase) => {
      if (contentLower.includes(phrase.phrase.toLowerCase())) {
        matches.push({
          phrase: phrase.phrase,
          severity: phrase.severity,
        });
      }
    });

    console.log('🔍 Blocked phrase check result:', { hasBlocked: matches.length > 0, matchCount: matches.length });
    
    return {
      hasBlocked: matches.length > 0,
      matches,
    };
  }
  
  const { data: phrases, error } = await supabase
    .from('blocked_phrases')
    .select('phrase, severity')
    .eq('is_active', true);

  if (error) throw error;

  const matches: Array<{ phrase: string; severity: string }> = [];
  const contentLower = content.toLowerCase();

  phrases?.forEach((phrase) => {
    if (contentLower.includes(phrase.phrase.toLowerCase())) {
      matches.push({
        phrase: phrase.phrase,
        severity: phrase.severity || 'medium',
      });
    }
  });

  return {
    hasBlocked: matches.length > 0,
    matches,
  };
};